import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { findRecipe } from "../data/recipes";
import { useUserRecipes } from "./useUserRecipes";

// useActiveCookSessions — array variant of useActiveCookSession.
//
// Returns every active cook the user can see (own + family + team),
// each enriched with recipe + active step + timer-end. Used to power
// the multi-cook banner stack — when multiple cooks are running in
// parallel (Trevor on steaks, Marissa on broccoli, both visible to
// each other), the stack lets either of them tap-switch focus into
// any of the running cooks without leaving CookMode.
//
// Why a sibling hook instead of extending useActiveCookSession:
// the singular hook owns the "single banner + offline cache + dismiss
// state" surface that's working today. Adding parallel-list semantics
// to it would tangle every consumer of the singular shape. The
// plural hook is read-only (no dismiss / cache plumbing) and only
// drives the secondary stack UI.
//
// Contract:
//   { cooks: [{ session, recipe, activeStep, timerEndsAt }], refresh, loading }
//
// Lightweight relative to the singular hook:
//   • No offline cache (the singular hook handles cold-start).
//   • No dismiss state (each cook in the stack is just a tap-target,
//     not a closeable card).
//   • Realtime is unfiltered on cook_sessions so any family-visible
//     change re-fetches; RLS scopes the result.

const QUERY_CUTOFF_MS = 30 * 60 * 60 * 1000; // wide net; matches singular hook
const MAX_COOKS = 10;

export function useActiveCookSessions(userId) {
  const [cooks, setCooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const { findBySlug: findUserRecipe } = useUserRecipes(userId);

  useEffect(() => {
    if (!userId) {
      setCooks([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const cutoff = new Date(Date.now() - QUERY_CUTOFF_MS).toISOString();
        // Two queries — own + team-participant — instead of trusting
        // RLS. cook_sessions has a family-wide read policy (any
        // family member can SELECT any family lead's session), so a
        // bare SELECT leaks ACTIVE COOKS to relatives who aren't
        // helping / dining. Mirrors useActiveCookSession's pattern.
        //
        // OWN sessions: I'm the lead.
        const ownReq = supabase
          .from("cook_sessions")
          .select("id, recipe_slug, recipe_title, recipe_emoji, started_at, status, user_id, step_summary, servings, cook_params")
          .eq("user_id", userId)
          .eq("status", "active")
          .gte("started_at", cutoff)
          .order("started_at", { ascending: false })
          .limit(MAX_COOKS);
        // TEAM sessions: I'm an active participant (helper/diner)
        // who hasn't left. The join keeps the result shape identical
        // to ownReq's so the enrichment loop below doesn't fork.
        const teamReq = supabase
          .from("cook_session_participants")
          .select("cook_sessions!inner(id, recipe_slug, recipe_title, recipe_emoji, started_at, status, user_id, step_summary, servings, cook_params)")
          .eq("user_id", userId)
          .is("left_at", null)
          .eq("cook_sessions.status", "active")
          .gte("cook_sessions.started_at", cutoff)
          .order("cook_sessions(started_at)", { ascending: false })
          .limit(MAX_COOKS);
        const [ownRes, teamRes] = await Promise.all([ownReq, teamReq]);
        if (!alive) return;
        if (ownRes.error)  console.warn("[activeCooks] own query:",  ownRes.error.message);
        if (teamRes.error) console.warn("[activeCooks] team query:", teamRes.error.message);
        const own  = ownRes.data || [];
        const team = (teamRes.data || []).map((r) => r.cook_sessions).filter(Boolean);
        // Merge by id, prefer the OWN row's identity when both
        // surface (lead viewing their own team-cook).
        const seen = new Set();
        const sessions = [];
        for (const s of [...own, ...team]) {
          if (!s?.id || seen.has(s.id)) continue;
          seen.add(s.id);
          sessions.push(s);
        }
        // Re-sort by started_at desc to honor the multi-cook stack
        // ordering (most recent on top).
        sessions.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
        const trimmed = sessions.slice(0, MAX_COOKS);
        if (!trimmed.length) {
          setCooks([]);
          setLoading(false);
          return;
        }
        const enriched = await Promise.all(
          trimmed.map(async (s) => {
            // Recipe resolution: cross-user sessions need the lead's
            // user_recipe (RLS lets the family see it). Fall through
            // to bundled / own-user resolver.
            let recipe = null;
            if (s.user_id && s.user_id !== userId) {
              try {
                const { data: ur } = await supabase
                  .from("user_recipes")
                  .select("recipe, slug")
                  .eq("slug", s.recipe_slug)
                  .eq("user_id", s.user_id)
                  .maybeSingle();
                if (ur?.recipe) {
                  recipe = { ...ur.recipe, slug: ur.slug, _userId: s.user_id };
                }
              } catch { /* fall through */ }
            }
            if (!recipe) recipe = findRecipe(s.recipe_slug, findUserRecipe, userId);
            // Slug missed — recover by stable recipe TITLE (see
            // useActiveCookSession for the full rationale).
            if (!recipe && s.recipe_title) {
              try {
                const { data: byTitle } = await supabase
                  .from("user_recipes")
                  .select("recipe, slug")
                  .eq("user_id", userId)
                  .eq("recipe->>title", s.recipe_title)
                  .order("updated_at", { ascending: false })
                  .limit(1);
                if (byTitle?.[0]?.recipe) recipe = { ...byTitle[0].recipe, slug: byTitle[0].slug };
              } catch { /* fall through */ }
            }

            // Source timerEndsAt from cook_session_steps.timer_state
            // (only set when user actually taps Start) instead of
            // cook_step_notifications.deliver_at (auto-queued on step
            // entry, which falsely made the bar tick for un-started
            // timers).
            const { data: stepRows } = await supabase
              .from("cook_session_steps")
              .select("id, step_id, step_title, started_at, finished_at, nominal_seconds, timer_state")
              .eq("cook_session_id", s.id)
              .is("finished_at", null)
              .order("started_at", { ascending: false })
              .limit(1);
            const row = stepRows?.[0] || null;
            const tsEndsAt = row?.timer_state?.endsAt;
            const tsPaused = !!row?.timer_state?.isPaused;
            return {
              session: s,
              recipe,
              activeStep: row,
              timerEndsAt: tsEndsAt && !tsPaused ? tsEndsAt : null,
            };
          }),
        );
        if (!alive) return;
        // Don't filter out cooks whose recipe can't resolve — the
        // session row itself carries enough info (recipe_title +
        // recipe_emoji) to render a card. A user_recipe that's still
        // loading from useUserRecipes' async fetch would otherwise
        // make every active cook disappear from the conductor sheet
        // until the recipe cache hydrated. Card components fall back
        // to session.recipe_title / .recipe_emoji when recipe is null.
        console.log("[activeCooks] enriched cooks:", enriched.length,
          "with-recipe:", enriched.filter((c) => c.recipe).length);
        setCooks(enriched);
        setLoading(false);
      } catch (e) {
        console.warn("[activeCooks] threw:", e?.message || e);
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, tick, findUserRecipe]);

  // Realtime: any change to cook_sessions / steps / notifications
  // re-runs the fetch. Single channel, unfiltered (RLS scopes the
  // delivered rows). Refresh is debounced naturally because tick
  // batches React state updates.
  useEffect(() => {
    if (!userId) return undefined;
    const ch = supabase
      .channel(`active-cooks:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_sessions" },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps" },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_step_notifications" },
        () => refresh(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, refresh]);

  return { cooks, refresh, loading };
}
